import { create } from 'zustand';
import { BestiaryEntry, BestiaryState } from '../types/bestiary.types';

const STORAGE_KEY = 'usagi_bestiary';

interface BestiaryActions {
  /**
   * Creates the entry if absent (with `firstSeenFloor`), otherwise increments
   * `kills`.
   */
  recordKill: (configId: string, floor: number) => void;
  getEntry: (configId: string) => BestiaryEntry | undefined;
  getAllEntries: () => BestiaryEntry[];
  resetBestiary: () => void;
}

function load(): BestiaryState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<BestiaryState>;
      return { entries: parsed.entries ?? {} };
    }
  } catch {
    /* ignore */
  }
  return { entries: {} };
}

function persist(state: BestiaryState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ entries: state.entries }));
}

export const useBestiaryStore = create<BestiaryState & BestiaryActions>(
  (set, get) => ({
    ...load(),

    recordKill: (configId, floor) => {
      set((s) => {
        const existing = s.entries[configId];
        const entry: BestiaryEntry = existing
          ? { ...existing, kills: existing.kills + 1 }
          : { configId, firstSeenFloor: floor, kills: 1 };
        const next = { entries: { ...s.entries, [configId]: entry } };
        persist(next);
        return next;
      });
    },

    getEntry: (configId) => get().entries[configId],

    getAllEntries: () => Object.values(get().entries),

    resetBestiary: () => {
      const reset = { entries: {} };
      set(reset);
      persist(reset);
    },
  }),
);
